/**
 * Shared "collective" build-up amount calculations for Retaining Walls and
 * Geo/Tobermore Walls.
 *
 * This is the SINGLE calculation for a wall spec's / screen-wall field's
 * priced amount, extracted out of `RetScreenWallsBillSummary.tsx` and
 * `GEOToberWallBillSummary.tsx` (their Collective view's per-spec
 * `specComputed` / screen-wall totals, byte-for-byte the same math those
 * components used to compute inline). Plot Matrix (`plotMatrixCategoryGenerator.ts`)
 * calls these same functions instead of re-deriving netPerM/rates itself —
 * previously Plot Matrix had its own separate implementation
 * (`buildRetainingWallNetPerMetreMap`) that could silently drift from what
 * the Bill Summary actually displayed (e.g. a different rate-fallback order),
 * causing the Plot Matrix Retaining Walls / Geowall & Tobermore Walls total
 * to disagree with the Bill Summary / BoQ Sum Analysis for the same data.
 *
 * `markupFactor` is a plain multiplier baked into the rate before rounding
 * (matches every Bill Summary's own `qty × rate.toFixed(2)` convention) —
 * pass the real OH&P/Preliminaries factor for Bill Summary use, or `1` for
 * Plot Matrix, which applies its own uplift uniformly afterward
 * (`applyPlotMatrixUplift`) so cells aren't marked up twice.
 */

import {
  wallSpecifications,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  resolveWallsBuildUpItemCode,
  isFacingBrickworkItem,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
} from "../mock/walls-section-build-up";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../mock/geo-tober-wall-build-up";
import { SCREEN_WALL_BOQ_FIELDS, getScreenWallFieldCode } from "./retainingWallsReferenceMapping";
import { buildDescriptionToIdQueue } from "./wallsBuildUpSave";

/** A build-up item as read off either the persisted API spec or local dimension state — only the fields the pricing math needs. */
export interface WallBuildUpDimensionItem {
  id?: string;
  description: string;
  net: number;
}

export interface WallSpecItemAmount {
  id: string;
  description: string;
  /** Raw `net` quantity per linear metre of wall, as read off the build-up item (signed). */
  net: number;
  /** Resolved per-unit rate (saved override, else blockwork/library rate — signed). */
  rate: number;
  /** `net × rate`, signed (not absolute — matches what the Bill Summary's Individual view displays per item). */
  netRate: number;
  evaluateCode: string;
}

export interface WallSpecAmount {
  wallQtyM: number;
  roundedWallQtyM: number;
  /** Σ|item.net × baseRate| across this spec's build-up items — no markup. */
  netPerM: number;
  /** `netPerM × markupFactor`, unrounded. */
  ratePerM: number;
  /** `roundedWallQtyM × parseFloat(ratePerM.toFixed(2))`, 2dp — the amount the Bill Summary displays for this spec's row. */
  billSummaryAmount: number;
  items: WallSpecItemAmount[];
}

export interface ScreenWallFieldAmount {
  qty: number;
  roundedQty: number;
  rate: number;
  evaluateCode: string;
  /** `roundedQty × parseFloat(rate.toFixed(2))`, 2dp. */
  amount: number;
}

/**
 * Per-spec amounts for the GRW/DRW/CCRW retaining-wall families
 * (`wallSpecifications`). Mirrors `RetScreenWallsBillSummary.tsx`'s Collective
 * view `specComputed` computation exactly — persisted API spec items are the
 * only item source (no local-state fallback), matching that component and
 * `calculateRetScreenWallsGrandTotal` (Sum Analysis).
 */
export function computeRetainingWallSpecAmounts(
  retainingWallsTable: Array<Record<string, unknown>> | undefined,
  wallsBuildUpData: Record<string, Array<{ id: string; rate: number }>> | undefined,
  wallsBuildUpDimensions: Record<string, WallBuildUpDimensionItem[]> | undefined,
  evaluateRateMap: Map<string, number>,
  facingBrickworkLabel: string | undefined,
  markupFactor: number,
): Map<string, WallSpecAmount> {
  const result = new Map<string, WallSpecAmount>();
  const tableData = retainingWallsTable || [];
  if (tableData.length === 0) return result;

  const buildUpData = wallsBuildUpData || {};
  const dimensionsBySpec = wallsBuildUpDimensions || {};

  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row) => {
    wallSpecifications.forEach((spec) => {
      wallTypeTotals[spec.id] = (wallTypeTotals[spec.id] || 0) + ((row[spec.id] as number) || 0);
    });
  });

  const descriptionToMockId = new Map<string, string>();
  wallSpecifications.forEach((spec) =>
    spec.items.forEach((mi) => descriptionToMockId.set(mi.description.toLowerCase(), mi.id)),
  );

  wallSpecifications
    .filter((spec) => (wallTypeTotals[spec.id] || 0) > 0)
    .forEach((spec) => {
      const savedItems = buildUpData[spec.id] || [];
      const wallQtyM = wallTypeTotals[spec.id] || 0;

      const rawItems = dimensionsBySpec[spec.id] ?? [];
      const itemsToUse = rawItems.filter((item) => {
        const desc = String(item.description ?? "").trim();
        return desc.length > 0 && !/^[0-9.]+$/.test(desc);
      });

      let netTotal = 0;
      const resolvedItems: WallSpecItemAmount[] = [];
      itemsToUse.forEach((item) => {
        const descKey = (item.description || "").toLowerCase();
        const mockId = descriptionToMockId.get(descKey);
        const persistedId = item.id || "";
        const derivedId = persistedId || (mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
        const isFacingBrick = isFacingBrickworkItem(derivedId);
        const evaluateCode = isFacingBrick
          ? (resolveWallsBuildUpItemCode(derivedId, facingBrickworkLabel) || "")
          : (WALLS_BUILD_UP_ITEM_CODE_MAP[derivedId] || "");
        const libraryRate = evaluateCode ? (evaluateRateMap.get(evaluateCode.trim().toUpperCase()) ?? 0) : 0;
        const savedItem = savedItems.find((s) => s.id === derivedId);
        const isMultiplied = isBlockworkBuildUpItem(derivedId);
        const rateMultiplier = getWallsBuildUpRateMultiplier(derivedId);
        const baseRate = isMultiplied
          ? libraryRate * rateMultiplier
          : savedItem && savedItem.rate !== 0
            ? savedItem.rate
            : libraryRate;
        const net = item.net ?? 0;
        const netRate = net * baseRate;
        netTotal += Math.abs(netRate);
        resolvedItems.push({ id: derivedId, description: item.description, net, rate: baseRate, netRate, evaluateCode });
      });

      const roundedWallQtyM = Math.round(wallQtyM);
      const ratePerM = netTotal * markupFactor;
      const billSummaryAmount = parseFloat((roundedWallQtyM * parseFloat(ratePerM.toFixed(2))).toFixed(2));

      result.set(spec.id, {
        wallQtyM,
        roundedWallQtyM,
        netPerM: netTotal,
        ratePerM,
        billSummaryAmount,
        items: resolvedItems,
      });
    });

  return result;
}

/**
 * Per-field amounts for the Screen Walls standalone take-off fields
 * (`SCREEN_WALL_BOQ_FIELDS`) — priced as `qty × library rate` independently
 * of any wall-spec build-up. Mirrors `RetScreenWallsBillSummary.tsx`'s
 * Collective view screen-wall computation and `calculateRetScreenWallsGrandTotal`'s
 * `screenWallsTotal` loop.
 */
export function computeScreenWallFieldAmounts(
  retainingWallsTable: Array<Record<string, unknown>> | undefined,
  evaluateRateMap: Map<string, number>,
  screenWallsLabel: string | undefined,
  eoPillarLabel: string | undefined,
  markupFactor: number,
): Map<string, ScreenWallFieldAmount> {
  const result = new Map<string, ScreenWallFieldAmount>();
  const tableData = retainingWallsTable || [];
  if (tableData.length === 0) return result;

  SCREEN_WALL_BOQ_FIELDS.forEach((field) => {
    const qty = tableData.reduce((sum, row) => sum + (Number(row[field.id]) || 0), 0);
    if (qty <= 0) return;
    const evaluateCode = getScreenWallFieldCode(field, screenWallsLabel, eoPillarLabel);
    const baseRate = evaluateRateMap.get(evaluateCode.trim().toUpperCase()) || 0;
    const rate = baseRate * markupFactor;
    const roundedQty = Math.round(qty);
    const amount = parseFloat((roundedQty * parseFloat(rate.toFixed(2))).toFixed(2));
    result.set(field.id, { qty, roundedQty, rate, evaluateCode, amount });
  });

  return result;
}

/**
 * Per-spec amounts for the GPCW/TPCW geo/tobermore-wall families
 * (`geoToberWallSpecifications`). Mirrors `GEOToberWallBillSummary.tsx`'s
 * Collective view `specComputed` computation exactly, including its
 * live-edit-takes-precedence-over-persisted item source and the
 * occurrence-queue id resolution for specs with duplicate item descriptions
 * (e.g. Tobermore TPCW's two "Stockpile Onsite" rows). Callers with no
 * "live vs persisted" distinction of their own (e.g. Plot Matrix, which
 * already reads a single merged/persisted-only `geoToberWallBuildUpDimensions`)
 * simply omit `liveDimensions`.
 */
export function computeGeoToberWallSpecAmounts(
  geoToberWallTable: Array<Record<string, unknown>> | undefined,
  geoToberWallBuildUpData: Record<string, Array<{ id: string; rate: number }>> | undefined,
  persistedDimensions: Record<string, WallBuildUpDimensionItem[]> | undefined,
  evaluateRateMap: Map<string, number>,
  markupFactor: number,
  liveDimensions?: Record<string, WallBuildUpDimensionItem[]>,
): Map<string, WallSpecAmount> {
  const result = new Map<string, WallSpecAmount>();
  const tableData = geoToberWallTable || [];
  if (tableData.length === 0) return result;

  const buildUpData = geoToberWallBuildUpData || {};

  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row) => {
    Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
      wallTypeTotals[specId] = (wallTypeTotals[specId] || 0) + ((row[fieldName] as number) || 0);
    });
  });

  geoToberWallSpecifications
    .filter((spec) => (wallTypeTotals[spec.id] || 0) > 0)
    .forEach((spec) => {
      const savedItems = buildUpData[spec.id] || [];
      const wallQtyM = wallTypeTotals[spec.id] || 0;
      const codeMap = spec.type === "geowall" ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;

      const liveItems = liveDimensions?.[spec.id];
      const rawItems = liveItems && liveItems.length > 0 ? liveItems : (persistedDimensions?.[spec.id] ?? []);
      const itemsToUse = rawItems.filter((item) => {
        const desc = String(item.description ?? "").trim();
        return desc.length > 0 && !/^[0-9.]+$/.test(desc);
      });

      const descriptionToMockIdQueue = buildDescriptionToIdQueue(spec.items);
      const descUsedCount = new Map<string, number>();

      let netTotal = 0;
      const resolvedItems: WallSpecItemAmount[] = [];
      itemsToUse.forEach((item) => {
        const descKey = (item.description || "").toLowerCase();
        const occurrence = descUsedCount.get(descKey) ?? 0;
        descUsedCount.set(descKey, occurrence + 1);
        const derivedId =
          item.id ?? descriptionToMockIdQueue.get(descKey)?.[occurrence] ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        const evaluateCode = codeMap[derivedId] || "";
        const libraryRate = evaluateCode ? (evaluateRateMap.get(evaluateCode.trim().toUpperCase()) ?? 0) : 0;
        const savedItem = savedItems.find((s) => s.id === derivedId);
        const baseRate = savedItem && savedItem.rate !== 0 ? savedItem.rate : libraryRate;
        const net = item.net ?? 0;
        const netRate = net * baseRate;
        netTotal += Math.abs(netRate);
        resolvedItems.push({ id: derivedId, description: item.description, net, rate: baseRate, netRate, evaluateCode });
      });

      const roundedWallQtyM = Math.round(wallQtyM);
      const ratePerM = netTotal * markupFactor;
      const billSummaryAmount = parseFloat((roundedWallQtyM * parseFloat(ratePerM.toFixed(2))).toFixed(2));

      result.set(spec.id, {
        wallQtyM,
        roundedWallQtyM,
        netPerM: netTotal,
        ratePerM,
        billSummaryAmount,
        items: resolvedItems,
      });
    });

  return result;
}
